import {
	ReactElement,
	useCallback
} from "react";
import { useRenderManager } from "../useRenderManager";
import { OneByOneTileContent } from "./OneByOneTileContent";
import { TwoByTwoTileContent } from "./TwoByTwoTileContent";
import { AppType } from "client/Api/Apps/common";
import { canUserAccessTile } from "client/Api/Workspace/workspace";
import { localize } from "client_legacy/ndaw/culture";
import { navigateToClientlessRdpApp } from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "./useRegisterTileUsage";
import { AlertManager, useAlerts } from "client/Hooks";
import { TileRenderState } from "../RenderState/common";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	useBaseNavigation,
	useConditionalAccessNavigation,
	useMaintenanceWindowNavigation
} from "./useInvokeNavigation";
import { TileContextProvider } from "../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type ClientlessRdpTileContentProps = {
	renderState: TileRenderState<AppType.ClientlessRdp>;
	onEdit: (() => void) | undefined;
	userTimezone: TimeZone;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const ClientlessRdpTileContent = (props: ClientlessRdpTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		userTimezone,
		onOpenTileMenu
	 } = props;

	const [navigationElement, navigateHandler] = useClientlessRdpTileNavigation(
		renderState,
		onEdit,
		userTimezone
	);

	const { dimension } = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<TwoByTwoTileContent
				onClick={navigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
ClientlessRdpTileContent.displayName = "ClientlessRdpTileContent";

export function useClientlessRdpTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.ClientlessRdp>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined,
	userTimezone: TimeZone
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);
	const alertManager: AlertManager = useAlerts();

	const {
		id,
		environmentAppId,
		settings: {
			destinationWindow
		},
		conditionalAccess,
		maintenanceWindow
	} = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useConditionalAccessNavigation(
			conditionalAccess,
			useMaintenanceWindowNavigation(
				maintenanceWindow,
				userTimezone,
				useCallback(
					async (): Promise<void> => {
						const hasAccess: boolean = await canUserAccessTile(id);

						if (!hasAccess) {
							alertManager.showError(localize("_NoAccess"));
							return;
						}

						navigateToClientlessRdpApp(
							{
								id: environmentAppId,
								destinationWindow
							},
							() => registerTileUsage()
						);
					},
					[
						alertManager,
						environmentAppId,
						id,
						registerTileUsage,
						destinationWindow
					]
				)
			)
		)
	);
}
